
import React, { useState } from 'react';
import { Edit3, Sparkles, Loader2, ArrowRight, Image as ImageIcon, Upload } from 'lucide-react';
import { SiteContent, DynamicStyle } from '../types';
import { optimizeContentAI } from '../services/geminiService';
import { uploadService } from '../services/uploadService';
import { MediaRenderer } from './MediaRenderer';

interface AboutProps {
  data: SiteContent['about'];
  isAdmin: boolean;
  onUpdate: (data: SiteContent['about']) => void;
  styles: Record<string, DynamicStyle>;
  onSelect: (id: string) => void;
  activeId: string | null;
}

const About: React.FC<AboutProps> = ({ data, isAdmin, onUpdate, styles, onSelect, activeId }) => {
  const [isOptimizing, setIsOptimizing] = useState<string | null>(null);
  const [isUploading, setIsUploading] = useState(false);

  const handleUpload = async () => {
    try {
      setIsUploading(true);
      const base64 = await uploadService.selectAndUpload();
      onUpdate({ ...data, imageUrl: base64 });
    } catch (err) {
      console.error('Upload failed:', err);
    } finally {
      setIsUploading(false);
    }
  };

  const getStyles = (id: string) => {
    const s = styles[id] || {};
    return {
      fontFamily: s.fontFamily,
      fontSize: s.fontSize,
      width: s.width,
      transform: s.scale ? `scale(${s.scale})` : undefined,
    };
  };

  const titleWords = data.title.split(' ');
  const mainTitle = titleWords.slice(0, 2).join(' ');
  const italicTitle = titleWords.slice(2).join(' ');

  const scrollToGallery = () => {
    document.getElementById('gallery')?.scrollIntoView({ behavior: 'smooth' });
  };

  return (
    <section id="discover" className="relative py-20 md:py-32 bg-white overflow-hidden scroll-mt-20" aria-labelledby="about-heading">
      <div className="absolute top-0 right-[-10%] md:right-0 w-[80%] md:w-[60%] h-full opacity-[0.02] md:opacity-[0.03] select-none pointer-events-none flex items-center justify-center">
        <span className="text-[15rem] md:text-[30rem] font-serif font-bold tracking-tighter text-slate-950 leading-none">01</span>
      </div>
      
      <div className="container mx-auto px-6 relative z-10">
        <div className="flex flex-col lg:flex-row items-center gap-12 lg:gap-24">
          
          <article className="w-full lg:w-1/2 relative group" style={getStyles('about-panel')}>
            <div 
              className={`bg-slate-950 p-3 md:p-4 rounded-[2.5rem] md:rounded-[3.5rem] shadow-2xl transition-all duration-700 hover:-translate-y-2 border border-white/5 ${isAdmin && activeId === 'about-panel' ? 'outline outline-4 outline-sky-400' : ''}`}
              onClick={() => onSelect('about-panel')}
            >
              <div className="relative overflow-hidden rounded-[2rem] md:rounded-[2.8rem] aspect-[4/5]">
                <MediaRenderer 
                  src={data.imageUrl} 
                  alt="Scenic view of volcanic rock formations and crystal clear waters on Balut Island, Sarangani" 
                  className="w-full h-full object-cover scale-105 group-hover:scale-100 transition-transform duration-[2s] opacity-90"
                  style={getStyles('about-img')}
                  onClick={(e) => { e.stopPropagation(); onSelect('about-img'); }}
                />
                
                <button onClick={scrollToGallery} className="absolute top-4 md:top-8 right-4 md:right-8 bg-white/10 backdrop-blur-xl border border-white/20 p-3 md:p-4 rounded-xl md:rounded-2xl text-white flex items-center gap-3 transition-all hover:bg-white hover:text-slate-950 group/btn shadow-2xl opacity-100 lg:opacity-0 lg:group-hover:opacity-100 translate-y-0 lg:translate-y-4 lg:group-hover:translate-y-0 duration-500" aria-label="View Balut Island photo gallery">
                  <ImageIcon className="w-4 h-4 md:w-5 md:h-5 group-hover/btn:rotate-12 transition-transform" />
                  <span className="text-[9px] md:text-[10px] font-bold uppercase tracking-luxury pr-1">Gallery</span>
                </button>
              </div>
              
              {isAdmin && (
                <div className="absolute inset-4 z-20 flex flex-col items-center justify-center bg-slate-950/80 opacity-0 group-hover:opacity-100 transition-opacity rounded-[2.8rem] gap-4">
                  <button onClick={handleUpload} disabled={isUploading} className="flex items-center gap-3 bg-white text-slate-950 px-8 py-4 rounded-full font-bold hover:bg-sky-500 hover:text-white transition-all text-[10px] tracking-luxury uppercase">
                    {isUploading ? <Loader2 className="w-4 h-4 animate-spin" /> : <Upload className="w-4 h-4" />}
                    Upload Media
                  </button>
                </div>
              )}
            </div>
          </article>

          <div className="w-full lg:w-1/2 mt-4 lg:mt-0">
            <div className="p-0 lg:p-4 rounded-[3.5rem] transition-all duration-700">
              {isAdmin ? (
                <div className="space-y-6">
                  <input value={data.badge} onChange={e => onUpdate({...data, badge: e.target.value})} className="w-full text-sky-500 font-bold tracking-luxury uppercase text-[10px] focus:outline-none bg-transparent" aria-label="Section Badge" />
                  <textarea 
                    value={data.title} 
                    onChange={e => onUpdate({...data, title: e.target.value})} 
                    className={`w-full text-3xl md:text-4xl font-bold text-slate-900 focus:outline-none font-serif italic bg-transparent leading-tight resize-none h-auto ${activeId === 'about-title' ? 'outline outline-2 outline-sky-400 p-2' : ''}`}
                    style={getStyles('about-title')}
                    onClick={() => onSelect('about-title')}
                    aria-label="Section Heading"
                  />
                  <textarea 
                    value={data.description1} 
                    onChange={e => onUpdate({...data, description1: e.target.value})} 
                    className={`w-full text-slate-600 text-base leading-relaxed font-light focus:outline-none h-32 bg-transparent ${activeId === 'about-desc' ? 'outline outline-2 outline-sky-400 p-2' : ''}`}
                    style={getStyles('about-desc')}
                    onClick={() => onSelect('about-desc')}
                    aria-label="Section Description"
                  />
                </div>
              ) : (
                <div className="space-y-8 md:space-y-12">
                  <div className="space-y-4 md:space-y-6">
                    <span className="text-sky-500 font-bold tracking-luxury uppercase text-[10px] block">{data.badge}</span>
                    <h2 id="about-heading" className="text-3xl md:text-5xl lg:text-6xl font-bold text-slate-900 leading-[1.15] font-serif tracking-tighter" style={getStyles('about-title')}>
                      {mainTitle} <span className="block italic font-light text-sky-500 mt-1 md:mt-2">{italicTitle}</span>
                    </h2>
                  </div>
                  <div className="space-y-6 md:space-y-8" style={getStyles('about-desc')}>
                    <p className="text-slate-600 leading-relaxed text-base md:text-lg font-light">{data.description1}</p>
                    <p className="text-slate-600 leading-relaxed text-base md:text-lg font-light border-l-4 border-sky-100 pl-6 md:pl-8 italic font-serif">{data.description2}</p>
                  </div>
                </div>
              )}
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};

export default About;
